import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Grid,
  FormControlLabel,
  Switch,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import config from "../config";
import { useNavigate, useParams } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify"; // Assume you'll use toast for notifications

const baseUrl = config.baseURL;
const steps = ["Module Info", "Add Fields", "Permissions"];

// Define the primary and accent colors for consistency
const PRIMARY_COLOR = "#2E4053"; // Dark Blue (from ModuleCreate)
const ACCENT_COLOR = "#2E4053"; // Using a distinct orange from the create button's hover for the primary action button to match the 'Create & Continue' button's *purpose*.

const ModuleEdit = () => {
  const { id } = useParams();
  const [token] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const navigate = useNavigate();

  const [moduleData, setModuleData] = useState({
    slug: "",
    name: "",
    description: "",
    is_active: true,
  });

  // --- 1. Fetch Existing Module Data ---
  useEffect(() => {
    if (!token || !id) {
      setLoading(false);
      return;
    }

    const fetchModule = async () => {
      try {
        const res = await axios.get(`${baseUrl}/api/module/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = res.data?.data || res.data;
        setModuleData({
          slug: data.slug || "",
          name: data.name || "",
          description: data.description || "",
          is_active: data.is_active !== undefined ? data.is_active : true,
        });
      } catch (err) {
        console.error("Error fetching module:", err);
        toast.error("Failed to load module data.");
        // Consider navigating to a fallback page on failure
      } finally {
        setLoading(false);
      }
    };

    fetchModule();
  }, [id, token]);

  // --- 2. Handle Update (PUT Request) ---
  const handleUpdateModule = async () => {
    // Basic validation
    if (!moduleData.name.trim()) {
      toast.error("Please provide a display name for the module.");
      return;
    }

    setIsSaving(true);
    try {
      await axios.put(`${baseUrl}/api/module/${id}`, moduleData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("Module updated successfully!");

      // Navigate to Step 2: Add Fields (using the existing ID)
      navigate(`/dashboard/modules/fields/${id}`);
    } catch (err) {
      console.error("Error updating module:", err);
      toast.error(err.response?.data?.message || "Failed to update module");
    } finally {
      setIsSaving(false);
    }
  };

  // --- 3. Handle Auto-Slug Update (Kept mostly for consistency, though slugs rarely change on edit) ---
  const handleNameChange = (e) => {
    const name = e.target.value;
    // Slug calculation is kept simple for edit, assuming slug is already set,
    // but updates the state for consistency if the component logic dictates it should change.
    const slug = name
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");

    setModuleData({ ...moduleData, name, slug });
  };

  if (loading) {
    return (
      <Box
        sx={{
          p: 4,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
          backgroundColor: "#F4F6F8", // Match page background
        }}
      >
        <CircularProgress sx={{ color: PRIMARY_COLOR }} />
      </Box>
    );
  }

  // --- JSX Rendering ---

  return (
    // Set a consistent background/padding for the overall page content
    <Box
      sx={{
        p: { xs: 2, sm: 4 }, // Responsive padding
        minHeight: "100vh",
        backgroundColor: "#F4F6F8", // A light, clean background
        fontFamily: "Poppins, sans-serif",
      }}
    >
      <Typography
        variant="h4"
        sx={{
          fontWeight: "600",
          mb: 4,
          color: PRIMARY_COLOR, // Use primary color for the title
        }}
      >
        ✏️ Edit Module: {moduleData.name || id}
      </Typography>

      {/* Stepper Styling */}
      <Stepper
        activeStep={0}
        sx={{
          mb: 5,
          // Customize the step color (dots/lines)
          "& .MuiStepIcon-root.Mui-active": {
            color: PRIMARY_COLOR,
          },
          "& .MuiStepIcon-root.Mui-completed": {
            color: PRIMARY_COLOR,
          },
          "& .MuiStepLabel-label": {
            fontWeight: 500,
            color: "#34495E",
          },
        }}
      >
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Card Styling */}
      <Card
        sx={{
          borderRadius: "12px",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)", // Subtle shadow
        }}
      >
        <CardContent>
          <Grid container spacing={3} direction="column">
            {/* Display Name */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Display Name"
                variant="outlined" // Explicitly use outlined variant
                value={moduleData.name}
                onChange={handleNameChange}
                // Apply color to the focused state
                sx={{
                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor: PRIMARY_COLOR,
                    },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: PRIMARY_COLOR,
                  },
                }}
              />
            </Grid>

            {/* Module Slug (auto-filled, disabled) */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Module Slug"
                variant="outlined"
                value={moduleData.slug}
                disabled
                // Style for disabled/read-only input
                sx={{
                  "& .MuiInputBase-input.Mui-disabled": {
                    backgroundColor: "#F0F0F0", // Light gray background
                    color: "#757575", // Darker text for readability
                  },
                }}
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                variant="outlined"
                multiline
                rows={3}
                value={moduleData.description}
                onChange={(e) =>
                  setModuleData({
                    ...moduleData,
                    description: e.target.value,
                  })
                }
                sx={{
                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor: PRIMARY_COLOR,
                    },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: PRIMARY_COLOR,
                  },
                }}
              />
            </Grid>

            {/* Active Toggle */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={moduleData.is_active}
                    onChange={(e) =>
                      setModuleData({
                        ...moduleData,
                        is_active: e.target.checked,
                      })
                    }
                    // Style the switch with the accent color
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: ACCENT_COLOR,
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        {
                          backgroundColor: ACCENT_COLOR,
                        },
                    }}
                  />
                }
                label={
                  <Typography sx={{ color: PRIMARY_COLOR, fontWeight: 500 }}>
                    Active
                  </Typography>
                }
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Action Button */}
      <Box sx={{ mt: 5 }}>
        <Button
          variant="contained"
          onClick={handleUpdateModule}
          disabled={isSaving}
          sx={{
            backgroundColor: ACCENT_COLOR, // Orange accent color
            color: "#FFFFFF", // White text
            padding: "12px 24px",
            fontSize: "16px",
            fontWeight: "bold",
            borderRadius: "8px",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#2E4053", // Darker orange for hover
              boxShadow: "0px 6px 15px rgba(234, 106, 18, 0.3)", // Orange shadow
            },
            minWidth: "200px",
          }}
        >
          {isSaving ? (
            <CircularProgress size={24} sx={{ color: "#FFFFFF" }} />
          ) : (
            "Update & Continue →"
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default ModuleEdit;
